import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'react-toastify';
import { parcelAPI } from '../../services/api';
import { format } from 'date-fns';

const fmtDate = (d, fmt = 'yyyy/MM/dd HH:mm') => (d ? format(new Date(d), fmt) : '—');
const fmt = (n) => Number(n || 0).toLocaleString('ar-SD', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const STATUS_LABELS = {
  draft: 'مسودة', payment_pending: 'بانتظار الدفع', payment_submitted: 'قيد المراجعة',
  paid: 'مدفوع', waiting_delivery: 'بانتظار التسليم', received_by_office: 'مستلم بالمكتب',
  waiting_transportation: 'بانتظار رحلة', loaded: 'محمّل', in_transit: 'في الطريق',
  arrived: 'وصل', ready_for_collection: 'جاهز للاستلام', completed: 'مكتمل',
  returned: 'مرتجع', cancelled: 'ملغي', rejected: 'مرفوض',
};

const StatCard = ({ label, value, accent }) => (
  <div className="bg-gray-800 rounded-2xl border border-gray-700 p-4">
    <p className="text-xs text-gray-400 font-semibold">{label}</p>
    <p className={`text-2xl font-black mt-1 tabular-nums ${accent || 'text-white'}`}>{value}</p>
  </div>
);

const INCIDENT_TYPE_LABELS = { damaged: 'تلف', lost: 'مفقود', delayed: 'تأخير', other: 'أخرى' };

// Platform-wide view of the same incidents companies see for their own
// parcels — super admin can step in on any of them (cross-company disputes,
// or a company that's slow to act).
const IncidentsPanel = () => {
  const [incidents, setIncidents] = useState([]);
  const [onlyOpen, setOnlyOpen] = useState(true);
  const [loading, setLoading] = useState(true);
  const [resolving, setResolving] = useState(null);
  const [resolution, setResolution] = useState('');
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await parcelAPI.getIncidents(onlyOpen ? 'open' : undefined);
      setIncidents(res.data.data || []);
    } catch { toast.error('خطأ في تحميل الحوادث'); }
    finally { setLoading(false); }
  }, [onlyOpen]);

  useEffect(() => { load(); }, [load]);

  const submitResolve = async () => {
    if (!resolving || !resolution.trim()) return toast.error('نص الحل مطلوب');
    setSaving(true);
    try {
      await parcelAPI.resolveIncident(resolving.id, { resolution: resolution.trim() });
      toast.success('تم تحديث الحادثة');
      setResolving(null); setResolution(''); load();
    } catch (err) {
      toast.error(err.response?.data?.message || 'خطأ في تحديث الحادثة');
    } finally { setSaving(false); }
  };

  return (
    <div className="bg-gray-800 rounded-2xl border border-gray-700 overflow-hidden mb-5">
      <div className="px-4 py-3 border-b border-gray-700 flex items-center justify-between flex-wrap gap-2">
        <h2 className="text-sm font-bold text-gray-200">حوادث الطرود — كل الشركات</h2>
        <button onClick={() => setOnlyOpen((v) => !v)} className="text-xs font-semibold text-blue-400 hover:underline">
          {onlyOpen ? 'عرض الكل' : 'عرض المفتوحة فقط'}
        </button>
      </div>
      {loading ? (
        <div className="py-10 text-center text-gray-500">جاري التحميل...</div>
      ) : incidents.length === 0 ? (
        <div className="py-10 text-center text-gray-500">لا توجد حوادث{onlyOpen ? ' مفتوحة' : ''}</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-900/40 text-gray-400">
              <tr>
                <th className="px-4 py-2.5 text-right font-medium">الطرد</th>
                <th className="px-4 py-2.5 text-right font-medium">الشركة</th>
                <th className="px-4 py-2.5 text-right font-medium">النوع</th>
                <th className="px-4 py-2.5 text-right font-medium">الوصف</th>
                <th className="px-4 py-2.5 text-right font-medium">الحالة</th>
                <th className="px-4 py-2.5 text-right font-medium">إجراء</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-700">
              {incidents.map((inc) => (
                <tr key={inc.id} className="text-gray-200 align-top hover:bg-gray-700/40">
                  <td className="px-4 py-2.5 font-mono font-bold text-blue-400">{inc.parcel?.trackingNumber}</td>
                  <td className="px-4 py-2.5 text-xs text-gray-400">{inc.parcel?.company?.name || '—'}</td>
                  <td className="px-4 py-2.5"><span className="px-2 py-0.5 rounded-full text-xs bg-red-900/40 text-red-300">{INCIDENT_TYPE_LABELS[inc.type] || inc.type}</span></td>
                  <td className="px-4 py-2.5 text-xs text-gray-300 max-w-xs">{inc.description}</td>
                  <td className="px-4 py-2.5">
                    <span className={`px-2 py-0.5 rounded-full text-xs ${inc.status === 'resolved' ? 'bg-emerald-900/40 text-emerald-300' : 'bg-amber-900/40 text-amber-300'}`}>
                      {inc.status === 'resolved' ? 'محلولة' : inc.status === 'investigating' ? 'قيد المتابعة' : 'مفتوحة'}
                    </span>
                  </td>
                  <td className="px-4 py-2.5">
                    {inc.status !== 'resolved' && (
                      <button onClick={() => { setResolving(inc); setResolution(''); }} className="bg-blue-700 hover:bg-blue-600 text-white text-xs font-bold px-3 py-1.5 rounded-lg">حل</button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {resolving && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" dir="rtl">
          <div className="bg-gray-800 border border-gray-700 rounded-2xl p-6 w-full max-w-md">
            <h3 className="font-bold text-lg mb-1 text-white">حل الحادثة</h3>
            <p className="text-xs text-gray-400 mb-4">#{resolving.parcel?.trackingNumber}</p>
            <textarea rows={3} value={resolution} onChange={(e) => setResolution(e.target.value)}
              placeholder="القرار/الحل..."
              className="w-full bg-gray-900 border border-gray-700 text-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
            <div className="flex gap-2 mt-5">
              <button onClick={submitResolve} disabled={saving} className="flex-1 bg-blue-700 hover:bg-blue-600 text-white rounded-xl py-2.5 text-sm font-bold disabled:opacity-60">
                {saving ? '...جارٍ الحفظ' : 'حفظ الحل'}
              </button>
              <button onClick={() => setResolving(null)} className="px-4 py-2.5 rounded-xl border border-gray-600 text-sm font-semibold text-gray-300">إلغاء</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default function SuperParcelsPage() {
  const [parcels, setParcels] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [status, setStatus] = useState('');
  const [loading, setLoading] = useState(true);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [listRes, analyticsRes] = await Promise.all([
        parcelAPI.getAll(status ? { status } : {}),
        parcelAPI.getAnalytics(),
      ]);
      setParcels(listRes.data.data || []);
      setAnalytics(analyticsRes.data.data);
    } catch { toast.error('خطأ في تحميل بيانات الطرود'); }
    finally { setLoading(false); }
  }, [status]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  return (
    <div dir="rtl" className="p-4 md:p-6">
      <div className="mb-5">
        <h1 className="text-xl font-bold text-white">الطرود — نظرة عامة</h1>
        <p className="text-xs text-gray-400 mt-0.5">تحليلات ومراقبة خدمة الطرود عبر كل الشركات</p>
      </div>

      {analytics && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
          <StatCard label="إجمالي الطرود" value={analytics.total} />
          <StatCard label="إجمالي الإيرادات" value={`${fmt(analytics.revenue)} ج.س`} accent="text-emerald-400" />
          <StatCard label="عدد الشركات المشاركة" value={analytics.byCompany?.length || 0} accent="text-blue-400" />
          <StatCard label="أنواع الطرود" value={Object.keys(analytics.byType || {}).length} accent="text-purple-400" />
        </div>
      )}

      <IncidentsPanel />

      {analytics?.byCompany?.length > 0 && (
        <div className="bg-gray-800 rounded-2xl border border-gray-700 overflow-hidden mb-5">
          <div className="px-4 py-3 border-b border-gray-700"><h2 className="text-sm font-bold text-gray-200">الإيرادات حسب الشركة</h2></div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-900/40 text-gray-400">
                <tr>
                  <th className="px-4 py-2.5 text-right font-medium">الشركة</th>
                  <th className="px-4 py-2.5 text-right font-medium">عدد الطرود</th>
                  <th className="px-4 py-2.5 text-right font-medium">الإيرادات</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-700">
                {analytics.byCompany.map((c) => (
                  <tr key={c.companyId} className="text-gray-200">
                    <td className="px-4 py-2.5">{c.companyName}</td>
                    <td className="px-4 py-2.5 tabular-nums">{c.count}</td>
                    <td className="px-4 py-2.5 tabular-nums text-emerald-400 font-semibold">{fmt(c.revenue)} ج.س</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <div className="bg-gray-800 rounded-2xl border border-gray-700 overflow-hidden">
        <div className="px-4 py-3 border-b border-gray-700 flex items-center justify-between flex-wrap gap-2">
          <h2 className="text-sm font-bold text-gray-200">جميع الطرود</h2>
          <select value={status} onChange={(e) => setStatus(e.target.value)} className="bg-gray-900 border border-gray-700 text-gray-200 rounded-xl px-3 py-1.5 text-xs">
            <option value="">كل الحالات</option>
            {Object.entries(STATUS_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </select>
        </div>
        {loading ? (
          <div className="py-16 text-center text-gray-500">جاري التحميل...</div>
        ) : parcels.length === 0 ? (
          <div className="py-16 text-center text-gray-500">لا توجد طرود</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-900/40 text-gray-400">
                <tr>
                  <th className="px-4 py-2.5 text-right font-medium">رقم التتبع</th>
                  <th className="px-4 py-2.5 text-right font-medium">الشركة</th>
                  <th className="px-4 py-2.5 text-right font-medium">الرحلة</th>
                  <th className="px-4 py-2.5 text-right font-medium">المبلغ</th>
                  <th className="px-4 py-2.5 text-right font-medium">الحالة</th>
                  <th className="px-4 py-2.5 text-right font-medium">تاريخ الإنشاء</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-700">
                {parcels.map((p) => (
                  <tr key={p.id} className="text-gray-200 hover:bg-gray-700/40">
                    <td className="px-4 py-2.5 font-mono font-bold text-blue-400">{p.trackingNumber}</td>
                    <td className="px-4 py-2.5">{p.company?.name || '—'}</td>
                    <td className="px-4 py-2.5 text-xs text-gray-400">{p.trip ? `${p.trip.fromCity} ← ${p.trip.toCity}` : '—'}</td>
                    <td className="px-4 py-2.5 tabular-nums">{fmt(p.totalPrice)} ج.س</td>
                    <td className="px-4 py-2.5"><span className="px-2.5 py-1 rounded-full text-xs font-semibold bg-gray-700 text-gray-200">{STATUS_LABELS[p.status] || p.status}</span></td>
                    <td className="px-4 py-2.5 text-xs text-gray-500">{fmtDate(p.createdAt, 'MM/dd HH:mm')}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
